import {Platform, TextStyle} from 'react-native';

import theme from '../../style/theme';
import type {TypographyWeights} from '../../types/theme';

/**
 * The font file basename for a weight, e.g. 'Rubik-SemiBold'.
 *
 * Moved here from app/components/ui-controls/Text.tsx, where it had no production
 * callers. Text.tsx re-exports it so its existing unit test keeps working.
 */
export function getFontByWeight(weight: keyof TypographyWeights): string {
  return theme.typography?.weights?.[weight]?.fileName ?? theme.typography?.fontFamily ?? 'Rubik';
}

/**
 * Font family and weight for one weight key, addressed the way the running platform
 * can actually resolve it.
 *
 * Android is the constrained platform. scripts/font-copy-helpers.js:37-55 generates
 * exactly two aliases per family, {family}.ttf and {family}_bold.ttf, and no
 * res/font/*.xml resource exists. ReactFontManager resolves numeric weight only via
 * that _bold suffix, so fontWeight '500' and '600' silently render Regular. Android
 * therefore addresses every weight by family name instead.
 *
 * Web registers all six weights against one family in web/static/css/fonts.css, so
 * numeric fontWeight works there. iOS registers the individual faces and also accepts
 * numeric weight against the base family.
 *
 * Branches on `Platform.OS`, not `Platform.select`: `Platform.select`'s implementation
 * is fixed per platform-specific module and never reads `Platform.OS`, so under the Jest
 * preset (which always resolves the iOS module) it cannot be driven into the android
 * branch by tests. Do not "modernise" this back to `Platform.select`.
 */
export function resolveWeight(weight: keyof TypographyWeights): TextStyle {
  if (Platform.OS === 'android') {
    return {fontFamily: getFontByWeight(weight)};
  }

  const fontWeight = String(
    theme.typography?.weights?.[weight]?.weight ?? 400,
  ) as TextStyle['fontWeight'];

  return {fontFamily: theme.typography?.fontFamily ?? 'Rubik', fontWeight};
}
